import { fetchTableData, refresh } from '../tables';
import { partners } from '../../../services/api.admin';
import { PERMISSIONS, hasPermissions } from '../../../constants/authentication/permissions';
import { currentUserSelector } from '../../../selectors/authentication';
import { fetchDependencies } from '../modal.forms';
import { ADMIN_PARTNERS_DETAILS_PATH } from '../../../constants/routes';
import { MODELS } from '../../../constants/admin/settings';
import { push } from 'react-router-redux';
import { apiMultipleCalls, apiActionCreator } from '../../helpers';
import { ADMIN_MODAL_FORMS, ADMIN_FLASH, ADMIN_TABLES } from '../../../constants/actions';
import { stateToApiData, apiDataToState } from '../../../selectors/admin/models/partners';
import { formDataSelector } from '../../../selectors/admin/forms';
import { forEach, filter } from 'lodash';
import { selectedSelector } from '../../../selectors/admin/tables';
import { addPartnersToClearanceCategories, removePartnersFromClearanceCategories } from './event.partner.clearance';

export function fetchEventPartners(eventId, pagination, contextId) {
    return fetchTableData(() => partners.getForEvent(eventId, pagination, contextId), 'partners', {
        pagination
    });
}

export function fetchEventPartnerDependencies(context, eventId) {
    return (dispatch, getState) => {
        const user = currentUserSelector(getState());
        const { events } = require('../../../services/api.admin');
        const dependencies = [];

        if (hasPermissions(user, PERMISSIONS.PARTNERS_VIEW)) {
            dependencies.push(context ? () => partners.getOrgAll(context) : partners.getAll);
        }

        if (eventId) {
            dependencies.push(() => events.getCategoryConfig(eventId));
        }

        dispatch(fetchDependencies(dependencies));
    };
}

export function createEventPartner(data, eventId) {
    return async (dispatch, getState) => {
        dispatch({ type: ADMIN_MODAL_FORMS.SUBMIT });

        try {
            const organizationId = formDataSelector(getState(), { model: MODELS.EVENT }).organizationId;
            const categoryConfig = formDataSelector(getState(), { model: MODELS.EVENT_CATEGORY_CONFIG });

            const partnerResponses = await Promise.all(data.partnerIds.map(partnerId => partners.getMember(partnerId)));

            const partnerData = partnerResponses.map(response => response.json || response);

            await Promise.all(
                partnerData.map(partner =>
                    partners.update(
                        partner.id,
                        stateToApiData({
                            ...partner,
                            organizationIds: [...(partner.organizationIds || []), organizationId],
                            eventIds: [...(partner.eventIds || []), eventId]
                        })
                    )
                )
            );

            const copyFromPartnerId = data.copyFromPartnerId;
            const shouldCopyVisibleCategories = copyFromPartnerId && data.copyVisibleCategories;
            const shouldCopyClearances = copyFromPartnerId && data.copyClearances;

            if (shouldCopyVisibleCategories && categoryConfig?.categories) {
                const visibleCategoryIds = categoryConfig.categories
                    .filter(category => {
                        if (category.visibleByDefault) return false;
                        const visibleByPartners = category.visibleByPartners || [];
                        return visibleByPartners.includes(copyFromPartnerId);
                    })
                    .map(cat => cat.id);
                await addPartnersToClearanceCategories(eventId, data.partnerIds, visibleCategoryIds);
            } else if (data.categoryIds?.length) {
                await addPartnersToClearanceCategories(eventId, data.partnerIds, data.categoryIds);
            }

            if (shouldCopyClearances) {
                await partners.copyClearance(copyFromPartnerId, data.partnerIds, eventId, data.copyFields || {});
            }

            dispatch({ type: ADMIN_MODAL_FORMS.SUCCESS });
            dispatch(refresh());

            const { fetchCategoryConfig } = require('./event.clearance');
            dispatch(fetchCategoryConfig(eventId));

            dispatch({
                type: ADMIN_FLASH.OPEN,
                message: `${data.partnerIds.length} media partner${
                    data.partnerIds.length === 1 ? '' : 's'
                } added to the event.`
            });
        } catch (error) {
            dispatch({ type: ADMIN_MODAL_FORMS.FAILED, error: error.message });
        }
    };
}

export function deleteEventPartners(eventId) {
    return (dispatch, getState) => {
        const tableIndex = 0;
        dispatch({ type: ADMIN_TABLES.DISABLE, index: tableIndex });
        const selected = selectedSelector(getState(), {});
        // TODO-NT Write a better back-end call to do this (bulk?)
        return apiMultipleCalls(
            dispatch,
            selected.map(s => () => partners.getMember(s)),
            resp => {
                const updateCalls = [];
                forEach(resp, p => {
                    const partnerData = apiDataToState(p);
                    partnerData.eventIds = filter(partnerData.eventIds, x => x !== eventId);
                    updateCalls.push(() => partners.update(partnerData.id, stateToApiData(partnerData)));
                });

                return apiMultipleCalls(dispatch, updateCalls, async () => {
                    await removePartnersFromClearanceCategories(eventId, selected);

                    dispatch({ type: ADMIN_TABLES.DELETE, index: tableIndex, removed: selected });
                    dispatch({
                        type: ADMIN_FLASH.OPEN,
                        message: `${selected.length} media partner${
                            selected.length === 1 ? '' : 's'
                        } removed from the event.`
                    });
                    dispatch(refresh());
                });
            },
            0,
            {},
            true
        );
    };
}

export function openEventPartner(ep) {
    return push(ADMIN_PARTNERS_DETAILS_PATH.replace(':partnerId', ep.id));
}

export function sendEmailToEventPartners(data, eventId) {
    return (dispatch, getState) => {
        dispatch({ type: ADMIN_MODAL_FORMS.SUBMIT });
        const selection = selectedSelector(getState());
        return apiActionCreator(
            dispatch,
            () => partners.sendMessage(eventId, selection, data.subject, data.text),
            () => {
                dispatch({ type: ADMIN_MODAL_FORMS.SUCCESS });
                dispatch({
                    type: ADMIN_FLASH.OPEN,
                    message: 'Mail successfully sent!'
                });
            }
        );
    };
}
